import React from "react";

import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { NEEDL_LINKS } from "../../constants/links";
import {
  getAuthenticationDescription,
  getAuthenticationRestrictionText,
} from "../utils";

import { LimitationNote } from "./shared/LimitationNote";
import { StepHeader } from "./shared/StepHeader";
import { StepLayout } from "./shared/StepLayout";
import { AzureStorageForm } from "./AzureStorageForm";
import { StepIndicator } from "./StepIndicator";
import { TelegramForm } from "./TelegramForm";

import type { ModalStepProps } from "../types/modal";

interface AuthenticateStepProps extends ModalStepProps {
  onSetAccountId?: (accountId: string) => void;
  showNonOAuthForm?: boolean;
}

export function AuthenticateStep({
  integration,
  integrationScope,
  onConnect,
  onSetAccountId,
  showNonOAuthForm = false,
  isLoading,
}: AuthenticateStepProps) {
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  const handleConnectClick = () => {
    onConnect?.();
  };

  const handleNonOAuthSuccess = (accountId: string) => {
    onSetAccountId?.(accountId);
  };

  const renderNonOAuthForm = () => {
    switch (integration.label) {
      case "azure_storage":
        return <AzureStorageForm onSuccess={handleNonOAuthSuccess} />;
      case "telegram":
        return <TelegramForm onSuccess={handleNonOAuthSuccess} />;
      default:
        throw new Error(`No form implemented for ${integration.value}`);
    }
  };

  return (
    <StepLayout
      header={
        <>
          <StepHeader integration={integration} />
          <StepIndicator
            currentStep="authenticate"
            sourceId={integration.label}
            integrationScope={integrationScope}
          />
        </>
      }
      bodyClassName="pt-6"
      footer={
        showNonOAuthForm ? undefined : (
          <Button
            className="w-full h-10"
            onClick={handleConnectClick}
            disabled={!termsAccepted || isLoading}
          >
            Sign in to {integration.value}
          </Button>
        )
      }
    >
      <div className="space-y-4">
        {!showNonOAuthForm ? (
          <>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-6">
              {getAuthenticationDescription(integration.label)}.{" "}
              <span className="font-semibold">
                Needl will not be able to write or modify any{" "}
                {getAuthenticationRestrictionText(integration.label)}
              </span>
              .
            </p>
            <LimitationNote sourceId={integration.label} />
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) =>
                  setTermsAccepted(checked as boolean)
                }
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium text-zinc-950 dark:text-white cursor-pointer"
              >
                By clicking Sign in, you agree to our{" "}
                <a
                  href={NEEDL_LINKS.TERMS_OF_SERVICE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href={NEEDL_LINKS.PRIVACY_POLICY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Privacy Policy.
                </a>
              </label>
            </div>
          </>
        ) : (
          renderNonOAuthForm()
        )}
      </div>
    </StepLayout>
  );
}
